<template>
  <div>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="customSearch(meals, headers, search)"
        hide-default-footer
        disable-pagination
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.shift1="{ item }">
          {{ formatShiftDisplay(item.shift1) }}
        </template>
        <template v-slot:item.shift2="{ item }">
          {{ formatShiftDisplay(item.shift2) }}
        </template>
        <template v-slot:item.shift3="{ item }">
          {{ formatShiftDisplay(item.shift3) }}
        </template>
        <template v-slot:item.shift4="{ item }">
          {{ formatShiftDisplay(item.shift4) }}
        </template>
        <template v-slot:item.total="{ item }">
          {{ formatTotalDisplay(item) }}
        </template>
        <template v-slot:body.append>
          <tr class="font-weight-bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td>{{ formatShiftDisplay(totals.shift1) }}</td>
            <td>{{ formatShiftDisplay(totals.shift2) }}</td>
            <td>{{ formatShiftDisplay(totals.shift3) }}</td>
            <td>{{ formatShiftDisplay(totals.shift4) }}</td>
            <td>{{ formatShiftDisplay(totals.total) }}</td>
          </tr>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyReportTabMeals",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  data() {
    return {
      report: null,
      loading: false,
    };
  },
  created() {
    this.fetchMeals();
  },
  methods: {
    fetchMeals() {
      this.loading = true;
      reportService
        .getMealsForRestaurant(this.restaurantId, {
          company: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatShiftDisplay(shiftData) {
      if (!shiftData) return "0";
      const quantity = shiftData.quantity || 0;
      const quantityPayed = shiftData.quantity_payed || 0;

      if (this.canCheckReport) {
        return `${quantity}/${quantityPayed}`;
      } else {
        return `${quantity}`;
      }
    },
    formatTotalDisplay(item) {
      const total = item.total || 0;
      const totalPayed = item.total_payed || 0;

      if (this.canCheckReport) {
        return `${totalPayed}/${total}`;
      } else {
        return `${total}`;
      }
    },
    createTotalsRow() {
      return {
        name: this.$t("total"),
        shift1: this.formatShiftDisplay(this.totals.shift1),
        shift2: this.formatShiftDisplay(this.totals.shift2),
        shift3: this.formatShiftDisplay(this.totals.shift3),
        shift4: this.formatShiftDisplay(this.totals.shift4),
        total: this.formatShiftDisplay(this.totals.total),
      };
    },
  },
  mounted() {
    this.fetchMeals();
  },
  computed: {
    meals() {
      if (!this.report) return [];
      const processedMeals = {};
      for (const companyName in this.report) {
        const shifts = this.report[companyName];
        for (const shiftName in shifts) {
          const mealsInShift = shifts[shiftName];
          const shiftKey = `shift${parseInt(shiftName, 10)}`;
          for (const mealName in mealsInShift) {
            const mealData = mealsInShift[mealName];
            if (!processedMeals[mealName]) {
              processedMeals[mealName] = {
                id: mealData.id,
                name: mealName,
                total: 0,
                total_payed: 0,
              };
            }
            if (!processedMeals[mealName][shiftKey]) {
              processedMeals[mealName][shiftKey] = {
                quantity: 0,
                quantity_payed: 0,
              };
            }
            processedMeals[mealName][shiftKey].quantity += mealData.quantity;
            processedMeals[mealName][shiftKey].quantity_payed += mealData.quantity_payed;
            processedMeals[mealName].total += mealData.quantity;
            processedMeals[mealName].total_payed += mealData.quantity_payed;
          }
        }
      }
      return Object.values(processedMeals);
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    totals() {
      const totals = {
        shift1: {
          quantity: 0,
          quantity_payed: 0,
        },
        shift2: {
          quantity: 0,
          quantity_payed: 0,
        },
        shift3: {
          quantity: 0,
          quantity_payed: 0,
        },
        shift4: {
          quantity: 0,
          quantity_payed: 0,
        },
        total: {
          quantity: 0,
          quantity_payed: 0,
        },
      };
      this.meals.forEach((meal) => {
        if (meal.shift1) {
          totals.shift1.quantity += meal.shift1.quantity;
          totals.shift1.quantity_payed += meal.shift1.quantity_payed;
        }
        if (meal.shift2) {
          totals.shift2.quantity += meal.shift2.quantity;
          totals.shift2.quantity_payed += meal.shift2.quantity_payed;
        }
        if (meal.shift3) {
          totals.shift3.quantity += meal.shift3.quantity;
          totals.shift3.quantity_payed += meal.shift3.quantity_payed;
        }
        if (meal.shift4) {
          totals.shift4.quantity += meal.shift4.quantity;
          totals.shift4.quantity_payed += meal.shift4.quantity_payed;
        }
        totals.total.quantity += [
          meal.shift1?.quantity,
          meal.shift2?.quantity,
          meal.shift3?.quantity,
          meal.shift4?.quantity,
        ]
          .filter(Boolean)
          .reduce((a, b) => a + b, 0);
        totals.total.quantity_payed += [
          meal.shift1?.quantity_payed,
          meal.shift2?.quantity_payed,
          meal.shift3?.quantity_payed,
          meal.shift4?.quantity_payed,
        ]
          .filter(Boolean)
          .reduce((a, b) => a + b, 0);
      });
      return totals;
    },
    canCheckReport() {
      const company = this.$store.state.user.user.companies[0];
      if (!company) return false;
      return !!company.checkReport;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.id)
        .join(",");
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: this.$t("meals"), value: "name" },
        { text: this.$t("shift_one"), value: "shift1" },
        { text: this.$t("shift_two"), value: "shift2" },
        { text: this.$t("shift_three"), value: "shift3" },
        { text: this.$t("shift_four"), value: "shift4" },
        { text: this.$t("total"), value: "total" },
      ];
    },
  },
  watch: {
    "dates.from"(newVal, oldVal) {
      this.fetchMeals();
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchMeals();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        const itemsWithTotals = [
          ...this.customSearch(this.meals, this.headers, this.search).map((meal) => ({
            ...meal,
            total: this.formatTotalDisplay(meal),
            shift1: this.formatShiftDisplay(meal.shift1),
            shift2: this.formatShiftDisplay(meal.shift2),
            shift3: this.formatShiftDisplay(meal.shift3),
            shift4: this.formatShiftDisplay(meal.shift4),
          })),
          this.createTotalsRow(),
        ];
        this.printDocument({
          tables: [
            {
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), this.$t("taken_meals")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const itemsWithTotals = [
          ...this.customSearch(this.meals, this.headers, this.search).map((meal) => ({
            ...meal,
            total: this.formatTotalDisplay(meal),
            shift1: this.formatShiftDisplay(meal.shift1),
            shift2: this.formatShiftDisplay(meal.shift2),
            shift3: this.formatShiftDisplay(meal.shift3),
            shift4: this.formatShiftDisplay(meal.shift4),
          })),
          this.createTotalsRow(),
        ];
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("taken_meals")].join(" ") + ".xlsx",
          sheets: [
            {
              name: [this.$t("report"), this.$t("taken_meals")].join(" "),
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              title: [this.$t("report"), this.$t("taken_meals")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
